import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useToggle } from 'react-use';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import {
  Alert,
  AlertWithSuppressionDialog,
} from 'src/dialogs/AlertWithSuppressionDialog';
import { usePriceStaggerFloorDialog } from 'src/dialogs/AlertWithSuppressionDialog/usePriceStaggerFloorDialog';
import { useConfirmationDialogSettings } from 'src/hooks/api/useConfirmationDialogSettings';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { isDatePassedHours } from 'src/utils/dateTimeUtils';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingGroupAutoPriceSettings,
  PricingClient,
  UiCurrency,
  UpdateListingGroupAutoPriceSettingsFromOnePagerRequest,
} from 'src/WebApiController';

import { PricingSettingAutoPriceBody } from './PricingSettingAutoPriceBody';
import { useAutoPricingInputFormHasChanges } from './PricingSettingAutoPriceGroup.hooks';

type PricingSettingAutoPriceGroupProps = {
  uiCurrency: UiCurrency;
  sellerFee: number | null;
  useVerticalView: boolean;
};

export const PricingSettingAutoPriceGroup = ({
  uiCurrency,
  sellerFee,
  useVerticalView,
}: PricingSettingAutoPriceGroupProps) => {
  const hasSellerEventAlertMutes = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );

  const confirmPriceStaggerDialog = useBasicDialog();
  const { confirmationDialogSettings } = useConfirmationDialogSettings();

  const { event, selectedGroup, selectedListingIds, flattenedListingGroup } =
    useInventoryEventDetailContext();
  const { watch, reset, handleSubmit, formState } =
    useFormContext<ListingGroupAutoPriceSettings>();
  const { activeAccountWebClientConfig } = useAppContext();
  const queryClient = useQueryClient();
  const { showErrorDialog } = useClientLogContext();
  const { refreshEventForItems } = useCatalogDataContext();
  const [isLoading, setLoading] = useToggle(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const startingUnitNetProceeds = watch('startingUnitNetProceeds');
  const undRelAmt = watch('undRelAmt');
  const undAbsAmt = watch('undAbsAmt');

  const priceStaggerViolatesFloors = useMemo(() => {
    if (!startingUnitNetProceeds || (!undRelAmt && !undAbsAmt)) {
      return false;
    }

    return flattenedListingGroup?.some((listing) => {
      const rank = listing.ltGrpPrior;
      if (!rank || !listing.procsFloor) {
        return false;
      }

      const premiumPrice = undRelAmt
        ? startingUnitNetProceeds * (1 + (rank - 1) * undRelAmt)
        : startingUnitNetProceeds + (rank - 1) * (undAbsAmt ?? 0);

      return premiumPrice < listing.procsFloor;
    });
  }, [flattenedListingGroup, startingUnitNetProceeds, undAbsAmt, undRelAmt]);

  const priceStaggerAlertDialog = usePriceStaggerFloorDialog(
    () => {
      handleSubmit(onSubmitHandler)();
    },
    () => {},
    event?.virtId
  );

  const {
    hasChangesInCompSettings,
    hasChangesInUndercutSettings,
    hasChangesInOutlierSettings,
    hasAnyPricingSettingChanges,
  } = useAutoPricingInputFormHasChanges();

  const onSubmitHandler = useCallback(
    async (autoPricingSectionForm: ListingGroupAutoPriceSettings) => {
      if (selectedGroup?.ltGrpId == null) {
        return;
      }

      await tryInvokeApi(
        async () => {
          setLoading(true);
          const result = await new PricingClient(
            activeAccountWebClientConfig
          ).updateListingGroupPricingSettingsFromOnePager(
            selectedGroup?.ltGrpId ?? undefined,
            event?.viagId ?? undefined,
            {
              autoPricingSettings: autoPricingSectionForm,
              hasChangesInCompSettings,
              hasChangesInUndercutSettings,
              hasChangesInOutlierSettings,
            } as UpdateListingGroupAutoPriceSettingsFromOnePagerRequest
          );

          if (isSuccess(result)) {
            reset(autoPricingSectionForm);
            await queryClient.invalidateQueries({
              queryKey: [
                'PricingClient.getListingGroupAutoPricingSettings',
                selectedGroup?.ltGrpId,
                selectedGroup?.viagEvId,
              ],
            });

            await refreshEventForItems(
              selectedListingIds,
              ActionOutboxEntityType.Listing
            );
          } else {
            showErrorDialog(
              'PricingClient.updateListingGroupPricingSettings',
              { message: result.message, status: result.status },
              {
                trackErrorData: {
                  autoPricingSectionForm,
                },
              }
            );
          }
        },
        async (error) => {
          showErrorDialog(
            'PricingClient.updateListingGroupPricingSettings',
            error,
            {
              trackErrorData: autoPricingSectionForm,
            }
          );
        },
        async () => {
          setLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      event?.viagId,
      hasChangesInCompSettings,
      hasChangesInOutlierSettings,
      hasChangesInUndercutSettings,
      queryClient,
      refreshEventForItems,
      reset,
      selectedGroup?.ltGrpId,
      selectedGroup?.viagEvId,
      selectedListingIds,
      setLoading,
      showErrorDialog,
    ]
  );

  const onSubmit = useCallback(() => {
    const isDialogEnabled =
      confirmationDialogSettings?.settings?.PriceStaggerFloorPriceDrop
        ?.isActive ?? true;

    const isDialogPastSuppressionWindow = isDatePassedHours(
      priceStaggerAlertDialog?.lastTimeStamp,
      1
    );

    if (
      hasSellerEventAlertMutes &&
      priceStaggerViolatesFloors &&
      priceStaggerAlertDialog &&
      isDialogEnabled &&
      isDialogPastSuppressionWindow
    ) {
      setAlerts([priceStaggerAlertDialog]);
      confirmPriceStaggerDialog.launchDialog();
    } else {
      handleSubmit(onSubmitHandler)();
    }
  }, [
    confirmationDialogSettings?.settings?.PriceStaggerFloorPriceDrop?.isActive,
    hasSellerEventAlertMutes,
    priceStaggerViolatesFloors,
    priceStaggerAlertDialog,
    confirmPriceStaggerDialog,
    handleSubmit,
    onSubmitHandler,
  ]);

  const onReset = useCallback(() => {
    reset({ ...(formState.defaultValues ?? {}) });
  }, [formState.defaultValues, reset]);

  const input = watch();

  const hasChanges = useMemo(() => {
    const defaultValues = formState.defaultValues;
    return (
      hasAnyPricingSettingChanges ||
      input.autoPricingEnabled !== defaultValues?.autoPricingEnabled ||
      input.netProceedsFloor !== defaultValues?.netProceedsFloor ||
      input.netProceedsCeiling !== defaultValues?.netProceedsCeiling ||
      input.undAbsAmt !== defaultValues?.undAbsAmt ||
      input.undRelAmt !== defaultValues?.undRelAmt ||
      input.startingUnitNetProceeds !== defaultValues?.startingUnitNetProceeds
    );
  }, [input, formState.defaultValues, hasAnyPricingSettingChanges]);

  const {
    compListingMode,
    outlierMode,
    undercutMode,
    undercutRelativeAmount,
    undercutAbsoluteAmount,
    outlierStandardDeviations,
    outlierKthLowestLimit,
    outlierKthLowestLimitAbsoluteSpacing,
    outlierKthLowestLimitRelativeSpacing,
  } = input;

  const hasSelectedSections = useMemo(() => {
    return (
      (input.compListingSelectedSectionSettings?.sectionIdFilter?.length ?? 0) >
      0
    );
  }, [input.compListingSelectedSectionSettings]);

  return (
    <>
      <PricingSettingAutoPriceBody
        isListingGroupForm
        compListingMode={compListingMode}
        outlierMode={outlierMode}
        undercutMode={undercutMode}
        undercutRelativeAmount={undercutRelativeAmount}
        undercutAbsoluteAmount={undercutAbsoluteAmount}
        outlierStandardDeviations={outlierStandardDeviations}
        outlierKthLowestLimit={outlierKthLowestLimit}
        outlierKthLowestLimitAbsoluteSpacing={
          outlierKthLowestLimitAbsoluteSpacing
        }
        outlierKthLowestLimitRelativeSpacing={
          outlierKthLowestLimitRelativeSpacing
        }
        hasChanges={hasChanges}
        hasSelectedSections={hasSelectedSections}
        isLoading={isLoading}
        uiCurrency={uiCurrency}
        sellerFee={sellerFee}
        useVerticalView={useVerticalView}
        onReset={onReset}
        onSubmit={onSubmit}
      />
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ConfirmPriceStagger} />}
        size="md"
        {...confirmPriceStaggerDialog.dialogProps}
        alerts={alerts}
        viagogoVirtualId={event?.virtId}
        onOkay={() => {
          return confirmPriceStaggerDialog.closeDialog();
        }}
        onCancel={() => confirmPriceStaggerDialog.closeDialog()}
      />
    </>
  );
};
